import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ColumnPicker from '../ColumnPicker';
import { computePropertyRollup, fmtMoney, fmtDateLocal, parseLocalDate } from '../../utils/propertyRollup';
import { exportToCsv } from '../../utils/exportCsv';
import { representedInYear } from '../../utils/retirement';
import { splitsForYear } from '../../utils/prorata';
import ValueYearSelect from '../ValueYearSelect';
import ScrollX, { pinCell, PINNED_TABLE, redrawKey } from '../ScrollX';

// Value Status priority ladder (lower rank = higher priority) — Steph's order.
const STATUS_ORDER = { 'Appeal Open': 1, 'Under Review': 2, 'Value Pending': 3, 'Appeal Closed': 4, 'Withdrawn': 5, 'Denied': 6, 'Value Accepted': 7 };

// Explicit column widths (key-based so they survive column-manager hide/reorder).
// table-layout:fixed honors these identically across thead and tbody.
// ⛔ EVERY COLUMN HAS ONE, THE PROPERTY NAME INCLUDED (Philip 2026-10-27). 'name' used to be omitted -> auto, taking
// the leftover width, and with every column on the leftover was nothing: names read "1...". Now the table is at least
// the sum of these and scrolls sideways (ScrollX) instead, with the name wrapping to two lines.
const COL_WIDTHS = {
  client: '160px',
  name: '240px',
  cm: '130px',
  cs: '130px',
  ts: '150px',
  loc_code: '110px',
  master_id: '110px',
  parcels: '90px',
  value_status: '140px',
  assessed: '140px',
  taxable_value: '140px',
  state: '90px',
  earliest_deadline: '150px',
};

// PropertiesGrid (Phase 2 Stage 2) — flat client-first table to the
// option-A-revised mockup: per-column search/autocomplete + chips, sortable
// headers (Value Status by ladder, money numeric), in-place parcel expander.
// Columns/order/visibility come from resolvedPropertyColumns (column manager);
// each descriptor carries { type, value, render }. AppShell renders the real
// header — this component renders only the page body.
export default function PropertiesGrid({
  c,
  s,
  isDark,
  showRetired,
  setShowRetired,
  currentValueYear,
  setValueYear,
  yearRange,
  propertiesLoaded,
  expandedProps,
  setExpandedProps,
  allProperties,
  parcels,
  valueByParcelId,
  prorataRows,
  allClients,
  resolvedPropertyColumns,
  savePropertyColumns,
  columnPickerOpen,
  setColumnPickerOpen,
  staffUser,
  addPropertyMsg,
  setAddPropertyOpen,
  navigateTo,
}) {
  const [filters, setFilters] = useState({});       // colKey -> query string
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterAt, setFilterAt] = useState(null);   // where the open filter popover sits (fixed, below its button)
  const [headerHovered, setHeaderHovered] = useState(false);

  // Close the filter popover on outside click / Escape.
  useEffect(() => {
    if (!openFilterKey) return undefined;
    function onDocClick(e) {
      const t = e.target;
      if (t.closest && (t.closest('[data-filter-pop]') || t.closest('[data-filter-btn]'))) return;
      setOpenFilterKey(null);
    }
    function onKey(e) { if (e.key === 'Escape') setOpenFilterKey(null); }
    // The popover is fixed on screen (a portal), so a scroll of the page or the table — which moves its column — closes it.
    function onScroll(e) { if (!(e.target.closest && e.target.closest('[data-filter-pop]'))) setOpenFilterKey(null); }
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('click', onDocClick); document.removeEventListener('keydown', onKey); document.removeEventListener('scroll', onScroll, true); };
  }, [openFilterKey]);

  // valueByParcelId: parcel_id -> { market, taxable } for the selected year — each parcel's final value
  // (utils/yearValues, the cards' and reports' rule), loaded by StaffPortal whenever the year changes.

  const clientNameById = new Map(allClients.map(cl => [cl.id, cl.name]));
  const clientById = new Map(allClients.map(cl => [cl.id, cl]));

  // Show-retired gates BOTH the property list and the parcels feeding rollups — by REPRESENTATION at the current value
  // year (retired_year cascade), never a stored 'Retired' status. Property shows if its own retirement allows the year
  // AND (it has no parcels OR ≥1 live parcel) — a parcel-less property isn't hidden; an all-retired one drops out.
  // ⛔ first_year IS IN HERE BECAUSE SQL HAS ALWAYS PASSED IT. Every RPC gates on
  // is_represented(pr.first_year, pr.retired_year, pr.reactivated_year, year) — this map fed the JS mirror
  // of that predicate while carrying only the two retirement columns, so the client and the server
  // disagreed about the property half of the cascade on every surface. retirement.js already states the
  // rule the RPCs implement ("a parcel cannot be ours before its building is"; representationStart takes
  // the LATER of the two) — the field was simply never handed over. Renaming the map would be honest but
  // touches nine call sites for no behavioural gain; the comment carries it instead.
  const propRetireById = new Map(allProperties.map(p => [p.id, { first_year: p.first_year, retired_year: p.retired_year, reactivated_year: p.reactivated_year }]));
  const parcelLiveIn = (p, year) => representedInYear(p, propRetireById.get(p.property_id), year);
  const parcelLive = (p) => parcelLiveIn(p, currentValueYear);
  const propHasParcels = new Set(parcels.map(p => p.property_id));
  const visibleParcels = showRetired ? parcels : parcels.filter(parcelLive);
  // ⛔ THE YEAR IS A PARAMETER SO THE EMPTY STATE CAN ASK ABOUT OTHER YEARS WITH THE SAME PREDICATE.
  // "N properties, represented from 2026" must be true of the list a reader will actually see when they
  // switch — a looser test here would point them at a year that also shows nothing, which is the dead
  // end this is removing rather than a smaller version of it. So shownIn() IS the filter, called with a
  // different year; it is not a second approximation of it.
  //
  // ⛔ AND IT IS COPIED VERBATIM, INCLUDING A KNOWN GAP: the property clause passes only
  // retired_year/reactivated_year, so a property's OWN first_year is ignored here (a property starting
  // 2026 with an undated parcel still lists in 2025 — it is the PARCELS that hide RecNation). The drill's
  // chip takes the opposite view, that the property outranks its parcels. Left alone deliberately:
  // honouring it would HIDE properties, a behavioural change of the same kind as the deferred Clients
  // gate (docs/known-debt.md), and diverging from it here would make the link disagree with the table.
  const shownIn = (p, year) =>
    representedInYear({ first_year: p.first_year, retired_year: p.retired_year, reactivated_year: p.reactivated_year }, null, year)
    && (!propHasParcels.has(p.id) || parcels.some(par => par.property_id === p.id && parcelLiveIn(par, year)));
  const propsToShow = showRetired ? allProperties : allProperties.filter(p => shownIn(p, currentValueYear));
  // The earliest configured year that would actually show something. null → nothing in range qualifies,
  // and the message degrades to "in other years" rather than printing a dead link.
  const firstYearWithProperties = (allProperties.length && !propsToShow.length)
    ? ((yearRange || []).find(y => allProperties.some(p => shownIn(p, y))) || null)
    : null;

  const visibleCols = resolvedPropertyColumns.filter(col => col.visible);
  // ⛔ CLIENT AND PROPERTY ARE PINNED ON THE LEFT, AND SO SHOWN FIRST (Philip 2026-10-27): they stay put while the
  // other columns scroll sideways. The other columns keep the column manager's order; the export does too.
  const PINNED = ['client', 'name'];
  const shownCols = [...PINNED.map(k => visibleCols.find(col => col.key === k)).filter(Boolean),
    ...visibleCols.filter(col => !PINNED.includes(col.key))];
  const EXPANDER_W = 32;
  const px = (k) => parseInt(COL_WIDTHS[k], 10);
  const pinLeft = {};
  let pinEnd = EXPANDER_W;
  shownCols.filter(col => PINNED.includes(col.key)).forEach(col => { pinLeft[col.key] = pinEnd; pinEnd += px(col.key); });
  const lastPinned = shownCols.filter(col => PINNED.includes(col.key)).slice(-1)[0]?.key;
  const pin = (key, opts) => (key in pinLeft ? pinCell(pinLeft[key], { ...opts, last: key === lastPinned }) : {});
  const tableMinWidth = EXPANDER_W + shownCols.reduce((sum, col) => sum + px(col.key), 0);

  // Pro-rata for the year (utils/prorata): a split parcel shows under each of the client's properties it has a share in.
  const splits = splitsForYear(prorataRows, parcels, currentValueYear);
  // Build the view-model up front so derived columns sort/filter correctly.
  const rows = propsToShow.map(p => {
    const r = computePropertyRollup(p, visibleParcels, valueByParcelId, splits);
    const clientName = clientNameById.get(p.client_id) || p.client_id || '';
    const ctx = { clientName, clientObj: clientById.get(p.client_id) || { id: p.client_id, name: clientName } };
    const scalars = {};
    for (const col of visibleCols) scalars[col.key] = col.value ? col.value(p, r, ctx) : '';
    return { p, r, ctx, scalars };
  });

  function uniqueValues(colKey) {
    const set = new Set();
    rows.forEach(row => { const v = row.scalars[colKey]; if (v !== '' && v != null) set.add(String(v)); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }

  const activeFilters = Object.entries(filters).filter(([, q]) => q && String(q).trim() !== '');

  let viewRows = rows.filter(row =>
    activeFilters.every(([k, q]) => String(row.scalars[k] ?? '').toLowerCase().includes(String(q).toLowerCase()))
  );
  if (sortKey) {
    const col = visibleCols.find(x => x.key === sortKey);
    const type = col && col.type;
    viewRows = [...viewRows].sort((a, b) => {
      const av = a.scalars[sortKey];
      const bv = b.scalars[sortKey];
      if (type === 'money' || type === 'num') return ((Number(av) || 0) - (Number(bv) || 0)) * sortDir;
      if (sortKey === 'value_status') return ((STATUS_ORDER[av] || 99) - (STATUS_ORDER[bv] || 99)) * sortDir;
      return String(av ?? '').localeCompare(String(bv ?? '')) * sortDir;
    });
  }

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => -d);
    else { setSortKey(key); setSortDir(1); }
  }
  function toggleExpanded(propId) {
    setExpandedProps(prev => { const next = new Set(prev); if (next.has(propId)) next.delete(propId); else next.add(propId); return next; });
  }
  function setFilter(key, val) { setFilters(prev => ({ ...prev, [key]: val })); }
  function clearFilter(key) {
    setFilters(prev => { const n = { ...prev }; delete n[key]; return n; });
    setOpenFilterKey(null);
  }

  // Inner parcel sub-table (Assessed relabeled to Market Value). `pct`: this property's pro-rata share of a split parcel
  // (undefined when the parcel isn't split that year) — tagged on the name, and its Market Value is that share.
  const PARCEL_COLUMNS = [
    { key: 'parcel_name', label: 'Parcel', align: 'left',
      render: (p, pct) => (
        <div>
          <div style={{ fontWeight: '600', color: 'var(--red)', fontSize: '12px' }}>{p.parcel_name || '—'}
            {pct != null && <span title={`Pro-rata: ${pct}% of this parcel counts toward this property in ${currentValueYear}`}
              style={{ marginLeft: '6px', fontSize: '10px', fontWeight: '700', color: 'var(--slate)', border: '1px solid var(--line)', borderRadius: 'var(--radius-pill)', padding: '0 6px' }}>Shared · {pct}%</span>}
          </div>
          {p.address && <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '1px' }}>{p.address}{p.city ? `, ${p.city}` : ''}</div>}
        </div>
      ),
    },
    { key: 'account_number', label: 'Account #', align: 'left',
      render: (p) => <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--slate)' }}>{p.account_number || '—'}</span> },
    { key: 'assessor', label: 'Assessor', align: 'left',
      render: (p) => <span style={{ fontSize: '12px', color: 'var(--slate)' }}>{p.assessor || '—'}</span> },
    { key: 're_pp', label: 'RE/PP', align: 'center',
      render: (p) => <span style={{ fontSize: '12px', color: 'var(--charcoal)' }}>{p.re_pp === 'Real Estate' ? 'RE' : 'PP'}</span> },
    { key: 'appeal_status', label: 'Status', align: 'left',
      render: (p) => p.appeal_status ? <span style={{ fontSize: '12px', color: 'var(--slate)' }}>{p.appeal_status}</span> : <span style={{ color: 'var(--muted)', fontSize: '12px' }}>—</span> },
    { key: 'assessed', label: 'Market Value', align: 'right',
      render: (p, pct) => { const mv = valueByParcelId.get(p.id)?.market; return <span title={pct != null && mv != null ? `${pct}% of ${fmtMoney(Number(mv))}` : undefined} style={{ fontSize: '12px', color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>{mv == null ? '—' : fmtMoney(Number(mv) * (pct ?? 100) / 100)}</span>; } },
    { key: 'appeal_deadline', label: 'Deadline', align: 'left',
      render: (p) => <span style={{ fontSize: '12px', color: 'var(--slate)' }}>{fmtDateLocal(parseLocalDate(p.appeal_deadline))}</span> },
  ];

  // ── styles ──
  const thBase = { fontFamily: "'Archivo',sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--slate)', padding: '14px 16px', borderBottom: '1.5px solid var(--line)', background: '#FCFCFD', whiteSpace: 'nowrap', position: 'relative' };
  const tdBase = { padding: '14px 16px', borderBottom: '1px solid var(--line-soft)', fontSize: '13.5px', lineHeight: '1.45', color: 'var(--charcoal)', verticalAlign: 'middle' };
  const subTh = { fontFamily: "'Archivo',sans-serif", fontSize: '9px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', padding: '8px 12px', borderBottom: '1px solid var(--line-soft)' };
  const subTd = { padding: '8px 12px', fontSize: '12px', color: 'var(--charcoal)', borderBottom: '1px solid var(--line-soft)', verticalAlign: 'middle' };
  const justify = (align) => align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start';
  const textAlign = (align) => align === 'right' ? 'right' : align === 'center' ? 'center' : 'left';

  const btnPrimary = { border: 'none', borderRadius: 'var(--radius-pill)', padding: '9px 18px', fontFamily: "'Public Sans',system-ui,sans-serif", fontWeight: '700', fontSize: '13px', cursor: 'pointer', background: 'var(--red)', color: '#fff', boxShadow: '0 2px 8px rgba(180,0,0,.22)', whiteSpace: 'nowrap' };
  const btnGhost = { border: '1px solid var(--line)', borderRadius: 'var(--radius-pill)', padding: '9px 16px', fontFamily: "'Public Sans',system-ui,sans-serif", fontWeight: '600', fontSize: '13px', cursor: staffUser?.id ? 'pointer' : 'default', background: 'var(--panel)', color: 'var(--charcoal)', whiteSpace: 'nowrap', opacity: staffUser?.id ? 1 : 0.5 };

  const filtersActive = activeFilters.length > 0;
  // ⛔ A BARE "3 properties" ON A YEAR-SCOPED PAGE DOES NOT SAY IT IS YEAR-SCOPED. The page filters on
  // the app-wide value year and had no year anywhere in its text and no control — so two RecNation
  // properties with first_year 2026 simply were not there on a 2025 view, and "3 properties across all
  // clients" read as the whole truth. Same ambiguity as the drill's "0 parcels"; here it was quieter,
  // because 3 looks like an answer in a way 0 does not.
  //
  // ⛔ THE TWO NUMBERS COME FROM DIFFERENT STAGES ON PURPOSE. The first is POST-column-filter (what is
  // on screen); the other-years count is PRE-filter (hidden by the YEAR, not by a filter), because it
  // answers "where is the rest of my portfolio" — a question a column filter has no bearing on. Mixing
  // them would make the two fail to add up whenever a filter is active.
  // ⛔ BOTH NUMBERS COME FROM allProperties, THE ARRAY THE TABLE IS BUILT FROM. A whole-table
  // `head: true` COUNT used to be passed in for this and was the bug: subtracting an in-memory filter
  // from a DB total gives a number relatable to neither, and on the Parcels page the same pairing
  // printed "Showing 3 of 3,795". Every count here is derived from the rows on screen by construction.
  const hiddenByYear = allProperties.length - propsToShow.length;
  const subhead = `${viewRows.length.toLocaleString()} ${viewRows.length === 1 ? 'property' : 'properties'}`
    + `${filtersActive ? ' matching filters' : ''} in ${currentValueYear}`
    + (hiddenByYear > 0 ? ` · ${hiddenByYear.toLocaleString()} in other years` : '');

  return (
    <div style={{ width: '100%' }}>
      {/* page head */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: '800', fontSize: '28px', color: 'var(--ink)', letterSpacing: '-0.01em' }}>Properties</div>
          {/* ⛔ NO optionChip HERE, AND THAT IS THE DECISION. On one property "is 2025 baseline?" has an
              answer; across a list it does not — one property starting 2026 beside three older ones makes
              2025 baseline for some and represented for others, and a single '·baseline' would be wrong in
              a helpful shape. A bare year is honest. See ValueYearSelect's header. */}
          <ValueYearSelect years={yearRange} value={currentValueYear} onChange={setValueYear} s={s} c={c} />
        </div>
        <div style={{ color: 'var(--slate)', fontSize: '13px', marginTop: '3px' }}>{subhead}</div>
      </div>

      {/* controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--slate)', fontWeight: '500', cursor: 'pointer' }}>
          <input type="checkbox" checked={showRetired} onChange={e => setShowRetired(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--red)', cursor: 'pointer' }} />
          Show retired
        </label>
        <button onClick={() => setAddPropertyOpen(true)} style={{ ...btnPrimary, marginLeft: 'auto' }}>+ Add Property</button>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setColumnPickerOpen(!columnPickerOpen)} disabled={!staffUser?.id}
            title={staffUser?.id ? 'Show, hide, and reorder columns' : 'Sign-in required to save column layout'}
            style={btnGhost}>
            ⊞ Columns
          </button>
          {columnPickerOpen && (
            <ColumnPicker
              columns={resolvedPropertyColumns}
              onSave={async (newCols) => { await savePropertyColumns(newCols); setColumnPickerOpen(false); }}
              onClose={() => setColumnPickerOpen(false)}
              c={c}
              s={s}
              isDark={isDark}
              disabled={!staffUser?.id}
            />
          )}
        </div>
        <button onClick={() => {
          const exportCols = visibleCols.filter(col => col.key !== 'earliest_deadline');
          const cols = exportCols.map(col => ({ key: col.key, label: col.label }));
          const exrows = viewRows.map(({ scalars }) => Object.fromEntries(exportCols.map(col => [col.key, scalars[col.key] ?? ''])));
          const d = new Date();
          const stamp = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
          exportToCsv('properties-' + stamp + '.csv', cols, exrows);
        }} style={btnGhost}>⬇ Export</button>
      </div>

      {addPropertyMsg && (
        <div style={{ padding: '10px 14px', marginBottom: '14px', background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '7px', fontSize: '12px', color: '#34d399' }}>
          {addPropertyMsg}
        </div>
      )}

      {/* active filter chips */}
      {filtersActive && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
          {activeFilters.map(([k, q]) => {
            const col = visibleCols.find(x => x.key === k);
            return (
              <div key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'var(--red-tint)', color: 'var(--red-dark)', borderRadius: 'var(--radius-pill)', padding: '5px 8px 5px 13px', fontSize: '12px', fontWeight: '600' }}>
                <span><b>{(col && col.label) || k}:</b> {q}</span>
                <span onClick={() => clearFilter(k)} style={{ cursor: 'pointer', width: '16px', height: '16px', borderRadius: '50%', background: 'rgba(180,0,0,0.15)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', lineHeight: 1 }}>×</span>
              </div>
            );
          })}
        </div>
      )}

      {/* table */}
      <div style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', overflow: 'visible' }}>
        <ScrollX>
        <table style={{ width: '100%', minWidth: `${tableMinWidth}px`, ...PINNED_TABLE, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: `${EXPANDER_W}px` }} />
            {shownCols.map(col => <col key={col.key} style={{ width: COL_WIDTHS[col.key] }} />)}
          </colgroup>
          <thead>
            <tr onMouseEnter={() => setHeaderHovered(true)} onMouseLeave={() => setHeaderHovered(false)}>
              <th style={{ ...thBase, ...pinCell(0, { background: '#FCFCFD', header: true, last: !lastPinned }) }}></th>
              {shownCols.map(col => {
                const sorted = sortKey === col.key;
                const filterActive = !!filters[col.key];
                const magVisible = headerHovered || filterActive;  // active filter stays visible regardless of hover
                const magStyle = { border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px', borderRadius: '5px', color: filterActive ? 'var(--red)' : '#B9B9C0', display: 'inline-flex', alignItems: 'center', lineHeight: 0, opacity: magVisible ? 1 : 0, pointerEvents: magVisible ? 'auto' : 'none', transition: 'opacity 0.15s ease' };
                const labelEl = <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(col.key)}>{col.label}</span>;
                const sortEl = <span onClick={() => toggleSort(col.key)} style={{ cursor: 'pointer', fontSize: '10px', lineHeight: 1, color: sorted ? 'var(--red)' : '#C4C4C9' }}>{sorted ? (sortDir === 1 ? '▲' : '▼') : '⇅'}</span>;
                const magBtn = (extra) => (
                  <button data-filter-btn onClick={(e) => {
                    e.stopPropagation();
                    const r = e.currentTarget.getBoundingClientRect();
                    setFilterAt({ left: Math.max(8, Math.min(r.left - 8, window.innerWidth - 236)), top: r.bottom + 8 });
                    setOpenFilterKey(prev => prev === col.key ? null : col.key);
                  }}
                    title={'Search ' + col.label}
                    style={{ ...magStyle, ...extra }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
                  </button>
                );
                return (
                  <th key={col.key} style={{ ...thBase, textAlign: textAlign(col.align), ...pin(col.key, { background: '#FCFCFD', header: true }) }}>
                    {col.align === 'center' ? (
                      // Center: label+sort truly centered; magnifier absolutely floated right (no layout impact at rest, fades in on hover).
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        {labelEl}{sortEl}
                        {magBtn({ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)' })}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: justify(col.align) }}>
                        {labelEl}{sortEl}{magBtn()}
                      </div>
                    )}
                    {/* ⛔ A PORTAL, position: fixed — the table now scrolls (ScrollX), and its box would clip a popover
                        positioned inside the header. */}
                    {openFilterKey === col.key && filterAt && createPortal(
                      <div data-filter-pop onClick={e => e.stopPropagation()}
                        style={{ position: 'fixed', top: `${filterAt.top}px`, left: `${filterAt.left}px`, zIndex: 1000, background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: '10px', boxShadow: '0 8px 28px rgba(20,20,30,.16)', padding: '8px', width: '220px', textTransform: 'none', letterSpacing: '0' }}>
                        <input autoFocus value={filters[col.key] || ''} placeholder={'Search ' + col.label + '…'}
                          onChange={e => setFilter(col.key, e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setOpenFilterKey(null); }}
                          style={{ width: '100%', border: '1px solid var(--line)', borderRadius: '7px', padding: '8px 10px', fontFamily: "'Public Sans',system-ui,sans-serif", fontSize: '13px', color: 'var(--charcoal)', fontWeight: '500', outline: 'none', boxSizing: 'border-box' }} />
                        <FilterSuggestions
                          col={col}
                          query={filters[col.key] || ''}
                          uniqueValues={uniqueValues}
                          rows={rows}
                          onPick={(v) => { setFilter(col.key, v); setOpenFilterKey(null); }}
                        />
                        <button onClick={() => clearFilter(col.key)}
                          style={{ marginTop: '6px', width: '100%', border: 'none', background: 'var(--line-soft)', color: 'var(--slate)', borderRadius: '7px', padding: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
                          Clear filter
                        </button>
                      </div>,
                      document.body,
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody key={redrawKey(viewRows.map(({ p }) => p.id))}>
            {viewRows.map(({ p, r, ctx }) => {
              const isOpen = expandedProps.has(p.id);
              // ⛔ RENAMED, BECAUSE THE PREDICATE NOW ADMITS A SECOND REASON. With first_year passed, this
              // is true for a year BEFORE we took the property on as well as one after we gave it up — and
              // those are opposites. Calling that "retired" is the conflation already corrected once on the
              // year tabs ("nothing was retired, we had not started"). The dimming is right either way: a row
              // every gate calls not-ours should not read as current.
              const notRepresented = !representedInYear({ first_year: p.first_year, retired_year: p.retired_year, reactivated_year: p.reactivated_year }, null, currentValueYear);
              return (
                <React.Fragment key={p.id}>
                  <tr style={{ opacity: notRepresented ? 0.6 : 1, transition: 'background .12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FCFAFA'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ ...tdBase, textAlign: 'center', cursor: 'pointer', ...pinCell(0, { last: !lastPinned }) }}
                      onClick={(e) => { e.stopPropagation(); toggleExpanded(p.id); }}>
                      <span style={{ color: 'var(--red)', display: 'inline-block', fontSize: '20px', fontWeight: '700', lineHeight: 1, cursor: 'pointer', transition: 'transform .15s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>›</span>
                    </td>
                    {shownCols.map(col => (
                      <td key={col.key} style={{ ...tdBase, textAlign: textAlign(col.align), cursor: (col.key === 'client' || col.key === 'name') ? 'pointer' : 'default', ...pin(col.key) }}
                        onClick={col.key === 'client' ? (e) => { e.stopPropagation(); navigateTo({ page:'clients', drillClient:ctx.clientObj }); } : col.key === 'name' ? (e) => { e.stopPropagation(); navigateTo({ page:'clients', drillClient:ctx.clientObj, drillProperty:p }); } : undefined}>
                        {(col.align === 'center' || col.align === 'right')
                          ? <div style={{ display: 'flex', justifyContent: justify(col.align), alignItems: 'center' }}>{col.render(p, r, ctx)}</div>
                          : col.render(p, r, ctx)}
                      </td>
                    ))}
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={shownCols.length + 1} style={{ padding: 0, background: '#FBFBFC', borderBottom: '1px solid var(--line-soft)' }}>
                        {r.parcels.length === 0 ? (
                          <div style={{ padding: '14px 22px', fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>No parcels linked to this property.</div>
                        ) : (
                          <table style={{ width: 'calc(100% - 28px)', margin: '0 0 0 28px', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>{PARCEL_COLUMNS.map(col => <th key={col.key} style={{ ...subTh, textAlign: textAlign(col.align) }}>{col.label}</th>)}</tr>
                            </thead>
                            <tbody>
                              {r.parcels.map(par => (
                                <tr key={par.id} style={{ cursor: 'pointer', opacity: parcelLive(par) ? 1 : 0.6 }}
                                  onClick={e => { e.stopPropagation(); navigateTo({ drillParcel:par }); }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'var(--line-soft)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  {PARCEL_COLUMNS.map(col => <td key={col.key} style={{ ...subTd, textAlign: textAlign(col.align) }}>{col.render(par, r.shareById.get(par.id))}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </ScrollX>
        {viewRows.length === 0 && (
          <div style={{ padding: '34px', textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>
            {/* ⛔ "Loading properties..." WAS SHOWN FOR EVERY EMPTY RESULT, not just a load in progress —
                so a year that filtered the whole table out reported that the app was still working. Worse
                than the drill's "No parcels found": that was true and useless, this was false, and it hid
                exactly the RecNation case (first_year 2026, invisible on 2025) behind a spinner's wording.
                Three states, told apart by data already in scope rather than guessed at. */}
            {(() => {
              if (filtersActive) return 'No properties match the current filters.';
              if (!propertiesLoaded) return 'Loading properties…';
              if (!allProperties.length) return 'No properties yet.';
              const n = allProperties.length;
              return (<>
                <div style={{ fontWeight: 600, color: c.text }}>No properties represented in {currentValueYear}.</div>
                <div style={{ marginTop: '4px' }}>
                  {n === 1 ? 'The one property on file is' : `All ${n.toLocaleString()} properties on file are`}
                  {firstYearWithProperties
                    ? <> represented from{' '}
                        <button type="button" onClick={() => setValueYear(firstYearWithProperties)}
                          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'var(--red)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                          {firstYearWithProperties}
                        </button>{' '}— switch to see {n === 1 ? 'it' : 'them'}.</>
                    : ' represented in other years.'}
                </div>
              </>);
            })()}
          </div>
        )}
      </div>

      {/* legend */}
      <div style={{ marginTop: '14px', fontSize: '11px', color: 'var(--muted)' }}>
        <b style={{ color: 'var(--slate)', fontWeight: '600' }}>Value Status</b> = highest-priority appeal stage across the property&rsquo;s parcels (Appeal Open &rsaquo; Under Review &rsaquo; Value Pending &rsaquo; Appeal Closed &rsaquo; Value Accepted)
      </div>
    </div>
  );
}

// Autocomplete list inside a column filter popover. enum columns suggest from
// distinct loaded values; text/money columns show a live distinct-value preview.
function FilterSuggestions({ col, query, uniqueValues, rows, onPick }) {
  const q = (query || '').trim().toLowerCase();
  let items;
  let emptyMsg = null;
  if (col.type === 'enum') {
    items = uniqueValues(col.key).filter(v => v.toLowerCase().includes(q));
    if (items.length === 0) emptyMsg = 'No matches';
  } else {
    items = [...new Set(rows.map(r => String(r.scalars[col.key] ?? '')).filter(v => v && v.toLowerCase().includes(q)))].slice(0, 6);
  }
  return (
    <div style={{ marginTop: '6px', maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1px' }}>
      {emptyMsg && <div style={{ padding: '7px 10px', fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>{emptyMsg}</div>}
      {items.map(v => (
        <div key={v} onClick={() => onPick(v)}
          style={{ padding: '7px 10px', minHeight: '28px', lineHeight: '1.3', borderRadius: '6px', fontSize: '12px', fontWeight: '500', color: 'var(--charcoal)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--red-tint)'; e.currentTarget.style.color = 'var(--red-dark)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--charcoal)'; }}>
          {v}
        </div>
      ))}
    </div>
  );
}
